// VisByShow.tsx
import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import { useLocation, useNavigate } from 'react-router-dom';
import detailedQuestions from '../detailed_questions.json';
import questions from '../questions.json'; 

type VisByShowInfo = {
  showID: string;
  rawData: Array<{ showID: string; ballotID: string; promptID: string; userInputData: string }>;
};

type VoteEntry = {
  ID: string;
  showID: string;
  userID: string;
  userID2: string;
  timestamp: string;
  ballotID: string; // Equivalent to questionNumber
  promptID: string;
  userInputData: string;
};

type AggregatedData = {
  questionNumber: number;
  [promptName: string]: number;
};

const VisByShow: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as VisByShowInfo;
  const showID = state?.showID;
  const rawData = state?.rawData;

  const [chartData, setChartData] = useState<AggregatedData[]>([]);
  const [questionsList, setQuestionsList] = useState<string[]>([]);
  const colors = ['#ffb600', '#0095ff', '#00d37b', '#8900f2', '#ed0062', '#C244B7', '#4300ed'];

  useEffect(() => {
    if (!showID || !rawData) return;

    const aggregatedData: AggregatedData[] = [];
    const questionsSet = new Set<string>();

    rawData.forEach(({ ballotID, promptID }) => {
      if (showID === showID) {
        const questionNumber = parseInt(ballotID, 10);
        let dataPoint = aggregatedData.find(data => data.questionNumber === questionNumber);
        if (!dataPoint) {
          dataPoint = { questionNumber };
          aggregatedData.push(dataPoint);
        }
        if (!dataPoint[promptID]) {
          dataPoint[promptID] = 0;
        }
        dataPoint[promptID] = (dataPoint[promptID] as number) + 1;
        questionsSet.add(ballotID);
      }
    });

    setChartData(aggregatedData);
    setQuestionsList(Array.from(questionsSet));
  }, [showID, rawData]);


//  useEffect(() => {
//   if (!showID) return;

//   fetch('http://172.233.194.230:8080/mysecretkey/getallvotes')
//     .then(response => response.json())
//     .then((allVotes: VoteEntry[]) => {
        
//       // 1. Filter votes for this show
//       const showVotes = allVotes.filter(v => v.showID === showID);

//       // Data structure for all questions and their prompts
//       const counts: { [questionNum: number]: { [promptName: string]: number } } = {};
      
//       // Initialize all questions 1001-1025 with their prompts set to 0
//       for (let qNum = 1001; qNum <= 1025; qNum++) {
//         const question = detailedQuestions[qNum];
//         if (question?.Prompts) {
//             counts[qNum] = {};
//           Object.values(question.Prompts).forEach((prompt: any) => {
//             counts[qNum][prompt.Name] = 0;
//           });
//         }
//       }

//       // Populating counts data structure with votes for each prompt in each question
//       showVotes.forEach(vote => {
//         const qNum = parseInt(vote.ballotID);
//         // console.log("qNum: ", qNum)
//         if (qNum < 1026) {
//           const promptName = detailedQuestions[qNum]?.Prompts[vote.promptID]?.Name;
//           if (!counts[qNum]) {
//               counts[qNum] = {};
//           }
//           if (!counts[qNum][promptName]) {
//             counts[qNum][promptName] = 0;
//           }
//           counts[qNum][promptName]++;
//         }
//       });

//       console.log("counts: ", counts)
//       // Converting to a final array for the graph
//       const chartData = Object.entries(counts)
//         .map(([qNum, prompts]) => ({
//           questionNumber: parseInt(qNum),
//           ...prompts
//         }))
//         .sort((a, b) => a.questionNumber - b.questionNumber);

//         // console.log("chartData: ", chartData)
//         setChartData(chartData);

//     });
// }, [showID]);

const showIDs = Object.keys(chartData);
  
  const overviewPage = () => {
    navigate('/VisHelper');
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        const questionText = questions[label];
        // console.log("detailedQuestions: ", detailedQuestions)
        console.log("payload: ", payload)
        return (
            <div
              style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #444',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '16px',
              }}
            >
              <p
                style={{
                  margin: '0 0 8px 0',
                }}
              >
                {`Question ${label}: ${questionText}`}
              </p>
            {payload.map((entry: any, index: number) => (
            <p
            key={index}
            style={{
              color: entry.color,
              margin: '4px 0',
              fontSize: '14px',
            }}
          >
            <span>{entry.name}:</span>
            <span style={{ marginLeft: '12px', fontWeight: 'bold' }}>
              {entry.value} votes
            </span>
          </p>
          ))}
        </div>
      );
    }
    return null;
  };
  const promptNames = Array.from(
    new Set(
      chartData.flatMap(obj =>
        Object.keys(obj).filter(k => k !== 'questionNumber')
      )
    )
  );
    console.log("promptNames: ", promptNames);

  return (
    <div className="App">
      <header className="App-header" style={{ padding: '20px' }}>
        <h2
          style={{
            textAlign: 'center',
            marginBottom: '40px',
          }}
        >
          {showID ? `Voting Results for Show: ${showID}` : 'Voting Results'}
        </h2>
        
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={600}>
            <BarChart data={chartData}>
              <XAxis
                dataKey="questionNumber"
                tick={{ fontSize: 12 }}
                // interval={0}
                // label={{ value: 'Questions', position: 'insideBottomRight', offset: -60 }}
              />
              <YAxis
              tick={{ fontSize: 16 }}
              label={{ value: 'Votes', angle: -90, position: 'insideLeft' }}
            />
              <Tooltip 
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(128, 128, 128, 0.5)' }}
            />
              {promptNames.map((pName, idx) => (
                  <Bar 
                    key={`${pName}-${idx}`} 
                    dataKey={pName} 
                    name={pName}
                    stackId="a"
                    fill={colors[idx % colors.length]} 
                   />
                ))}     
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p>Loading data...</p>
        )}
        <button
          onClick={overviewPage}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#5b58aa',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            transition: 'background-color 0.3s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#6a6a7c')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#5b58aa')}
        >
          Back to Overview
        </button>
      </header>
    </div>
  );
};

export default VisByShow;